import React, { useState, useMemo } from "react";
import {
  Breadcrumb,
  Layout,
  theme,
  Select,
  Typography,
  Card,
  Button,
  Spin,
  message,
  Tooltip,
  Tag,
} from "antd";
import { useAuth } from "../hooks/useAuth";
import { useBenchmarkingTierAccess, useElementAccess } from "../hooks/useTierAccess";
import {
  useGetBrandsQuery,
  useGetOrgBrandBenchmarksQuery,
  useGetBenchmarkingPreferencesQuery,
  useUpdateBenchmarkingPreferencesMutation,
  useGetBenchmarkBusinessTypesQuery,
} from "../services/nestjs-base-api";
import {
  ReloadOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  RadarChartOutlined,
  LineChartOutlined,
  DotChartOutlined,
  NodeIndexOutlined,
  DashboardOutlined,
  SplitCellsOutlined,
  RiseOutlined,
  FallOutlined,
  GlobalOutlined,
  LockOutlined,
  CrownOutlined,
  FundOutlined,
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { useScopedNavigate } from '../components/MarketerView/useScopedNavigate';
import { useIntl } from 'react-intl';
import { PerformanceChart, PolarChart, MetricsCards, BulletChart, DivergingBarChart, SlopeChart, ScatterPlot, StripChart, BenchmarkMetric } from '../components/charts';
import { getPerformanceLevel, calculateAbsoluteDifference } from '../components/charts/benchmarkThresholds';
import { getDisplayName } from '../utils/elementDisplayNames';
import { MaturityTag } from '../components/MaturityTag';
import { MaturityAlert } from '../components/MaturityAlert';

const { Content } = Layout;
const { Title, Text } = Typography;

export interface BenchmarkResponse {
  brandPerformance: {
    overallScore: number;
    overallPercentile: number;
    metrics: BenchmarkMetric[];
    strengths: BenchmarkMetric[];
    weaknesses: BenchmarkMetric[];
  };
  distribution?: {
    sampleSize: number;
  };
}

interface BenchmarkingPageProps {
  /** When true, renders without the page chrome (breadcrumb/Layout) and brand
   *  picker — for embedding inside the Marketer View shell (SYR-68). */
  embedded?: boolean;
  /** Fixed brand id when embedded (replaces the page's own brand picker). */
  brandId?: number;
  /** Controlled time period (`{N}_days` / `all_time`). When set, the page's own
   *  period selector is hidden and the global Marketer View scope drives the
   *  window — keeps Brand Equity consistent across marketer tabs (SYR-68). */
  periodOverride?: string;
}

const BenchmarkingPage: React.FunctionComponent<BenchmarkingPageProps> = ({
  embedded = false,
  brandId: brandIdProp,
  periodOverride,
}) => {
    const { user } = useAuth();
    const navigate = useScopedNavigate();
    const intl = useIntl();
    const [searchParams] = useSearchParams();

    // Read initial values from URL params (for deep linking from dashboard)
    // If no URL param, sync with Data Selection period from localStorage
    const getInitialTimePeriod = (): string => {
        const urlParam = searchParams.get('timePeriod');
        if (urlParam) return urlParam;

        try {
            const storageKey = `brandEquityFilters_${user?.id || 'default'}`;
            const saved = localStorage.getItem(storageKey);
            if (saved) {
                const parsed = JSON.parse(saved);
                const periodMap: Record<string, string> = {
                    'last30': '30_days',
                    'last60': '60_days',
                    'last90': '90_days',
                    'last120': '120_days',
                    'last180': '180_days',
                    'last365': '365_days',
                    'allTime': 'all_time',
                };
                if (parsed.periodType && periodMap[parsed.periodType]) {
                    return periodMap[parsed.periodType];
                }
            }
        } catch (e) {
            // Fall through to default
        }
        return 'all_time';
    };
    const initialTimePeriod = getInitialTimePeriod();
    const initialBusinessType = searchParams.get('businessType') || 'all';
    const initialProductType = searchParams.get('productType') || 'all';
    const initialRegion = searchParams.get('region') || 'all';

    const [selectedBrandId, setSelectedBrandId] = useState<number>();
    // When embedded, the brand is fixed via props; otherwise the page's picker drives it.
    const effectiveBrandId = embedded ? brandIdProp : selectedBrandId;
    const [timePeriod, setTimePeriod] = useState(initialTimePeriod);
    // When a controlled period is supplied (Marketer View), it is the single
    // source of truth for the window; the local selector is hidden.
    const effectiveTimePeriod = periodOverride ?? timePeriod;
    const [businessTypeFilter, setBusinessTypeFilter] = useState(initialBusinessType);
    const [productTypeFilter, setProductTypeFilter] = useState(initialProductType);
    const [selectedCountry, setSelectedCountry] = useState<string>(initialRegion);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isUpdatingLatentDisplay, setIsUpdatingLatentDisplay] = useState(false);
    const [isUpdatingMeasuredDisplay, setIsUpdatingMeasuredDisplay] = useState(false);

    // Tier access for benchmarking features
    const { hasOverall, hasProductType, hasAllLevels, isLoading: tierLoading } = useBenchmarkingTierAccess();

    // Element access for showing locked elements in charts
    const { isElementAllowed, hasUnrestrictedAccess } = useElementAccess();

    // Helper to check if element is locked (inverse of allowed)
    const isElementLocked = React.useCallback((elementName: string) => {
      if (hasUnrestrictedAccess) return false;
      // For benchmarking, we check both latent and measured elements
      // Level 1 metrics are latent elements, Level 2 are measured elements
      return !isElementAllowed(elementName, 'latent') && !isElementAllowed(elementName, 'questionCategory');
    }, [hasUnrestrictedAccess, isElementAllowed]);

    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    const { data: brandsData } = useGetBrandsQuery(
        { jwtToken: user!.jwtToken, organisationId: user!.organisation.id },
        { skip: !user?.jwtToken }
    );

    const brands = useMemo(() => brandsData || [], [brandsData]);
    const selectedBrand = brands.find(b => b.id === effectiveBrandId);

    // Display preferences
    const { data: preferences } = useGetBenchmarkingPreferencesQuery({
      jwtToken: user?.jwtToken || '',
    });
    const [updatePreferences] = useUpdateBenchmarkingPreferencesMutation();

    const displayPreferences = preferences?.displayTypes || {
      latentElements: 'cards',
      measuredElements: 'bar_chart'
    };

    // Fetch configurable business types (includes productTypes relation)
    const { data: businessTypesData } = useGetBenchmarkBusinessTypesQuery(
      { jwtToken: user?.jwtToken || '' },
      { skip: !user?.jwtToken },
    );

    // Build business type dropdown options: "All Companies" + brand's own business type
    const businessTypeOptions = useMemo(() => {
      const allOption = { value: 'all', label: intl.formatMessage({ id: 'benchmarking.filter.allCompanies', defaultMessage: 'All Companies' }) };
      if (!businessTypesData || !selectedBrand?.businessTypeId) return [allOption];

      const brandBT = businessTypesData.find((bt: any) => bt.id === selectedBrand.businessTypeId);
      if (brandBT) {
        return [allOption, { value: brandBT.slug, label: brandBT.name }];
      }
      return [allOption];
    }, [businessTypesData, selectedBrand, intl]);

    // Build product type options from the selected business type's linked product types
    const selectedBusinessTypeEntity = useMemo(() => {
      if (businessTypeFilter === 'all' || !businessTypesData) return null;
      return businessTypesData.find((bt: any) => bt.slug === businessTypeFilter) || null;
    }, [businessTypesData, businessTypeFilter]);

    const productTypeOptions = useMemo(() => {
      const allOption = { value: 'all', label: intl.formatMessage({ id: 'benchmarking.filter.allProductTypes', defaultMessage: 'All Product Types' }) };
      if (!selectedBusinessTypeEntity?.productTypes?.length) return [allOption];
      return [allOption, ...selectedBusinessTypeEntity.productTypes.map((pt: any) => ({ value: pt.slug, label: pt.name }))];
    }, [selectedBusinessTypeEntity, intl]);

    const isProductTypeDisabled = businessTypeFilter === 'all';

    // Reset product type when business type changes to 'all' or when product type is no longer valid
    React.useEffect(() => {
      if (businessTypeFilter === 'all') {
        setProductTypeFilter('all');
      } else {
        const isValid = productTypeOptions.some(o => o.value === productTypeFilter);
        if (!isValid) setProductTypeFilter('all');
      }
    }, [businessTypeFilter, productTypeOptions, productTypeFilter]);

    const { data: benchmarkResponse, isLoading: benchmarkLoading, refetch: refetchBenchmarkData } = useGetOrgBrandBenchmarksQuery({
      jwtToken: user?.jwtToken || '',
      brandId: effectiveBrandId || 0,
      query: { timePeriod: effectiveTimePeriod, businessType: businessTypeFilter, productType: productTypeFilter, includeHistorical: true }
    }, { skip: !effectiveBrandId || !user?.jwtToken });

    const benchmarkData = useMemo(() => {
      const data = benchmarkResponse?.data;
      if (!data) return undefined;
      const mapMetrics = (metrics: BenchmarkMetric[]) =>
        metrics.map(m => ({ ...m, categoryName: getDisplayName(m.categoryName) }));
      return {
        ...data,
        brandPerformance: {
          ...data.brandPerformance,
          metrics: mapMetrics(data.brandPerformance.metrics),
          strengths: mapMetrics(data.brandPerformance.strengths),
          weaknesses: mapMetrics(data.brandPerformance.weaknesses),
        },
      };
    }, [benchmarkResponse?.data]);
    const benchmarkAvailable = benchmarkResponse?.available || false;
    const benchmarkUnavailableReason = benchmarkResponse?.reason;

    const TIME_PERIODS = [
      { value: 'all_time', label: 'All Time' },
      { value: '365_days', label: 'Last 365 Days' },
      { value: '180_days', label: 'Last 180 Days' },
      { value: '120_days', label: 'Last 120 Days' },
      { value: '90_days', label: 'Last 90 Days' },
      { value: '60_days', label: 'Last 60 Days' },
      { value: '30_days', label: 'Last 30 Days' },
    ];

    const getFilteredMetrics = (metrics: BenchmarkMetric[]): BenchmarkMetric[] => {
      if (!metrics) return [];

      const level0Metrics = metrics.filter(m => m.level === 0);
      const level1Metrics = metrics.filter(m => m.level === 1);
      const level2Metrics = metrics.filter(m => m.level === 2);

      const allMetrics = [...level0Metrics, ...level1Metrics, ...level2Metrics];

      const uniqueMetrics = new Map();
      allMetrics.forEach(metric => {
        if (metric.value !== null && metric.value !== undefined) {
          const key = `${metric.categoryName}_${metric.level}`;
          if (!uniqueMetrics.has(key)) {
            uniqueMetrics.set(key, metric);
          }
        }
      });

      return Array.from(uniqueMetrics.values());
    };

    // Auto-select first brand
    React.useEffect(() => {
        if (!embedded && brands.length > 0 && !selectedBrandId) {
            setSelectedBrandId(brands[0].id);
        }
    }, [embedded, brands, selectedBrandId]);

    // Reset business type filter if current selection is not valid for this brand
    React.useEffect(() => {
      const isCurrentSelectionValid = businessTypeOptions.some(option => option.value === businessTypeFilter);
      if (!isCurrentSelectionValid) {
        setBusinessTypeFilter('all');
      }
    }, [businessTypeOptions, businessTypeFilter]);

    // Deep-link from The Briefing's Q2: scroll to the Latent / Measured section.
    // Sections render after data loads, so retry across a few frames.
    React.useEffect(() => {
      const section = searchParams.get('section');
      const id =
        section === 'measured'
          ? 'benchmark-section-measured'
          : section === 'latent'
            ? 'benchmark-section-latent'
            : null;
      if (!id) return;
      let tries = 0;
      let raf = 0;
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        if (tries++ < 30) raf = requestAnimationFrame(tryScroll);
      };
      const t = window.setTimeout(tryScroll, 120);
      return () => {
        window.clearTimeout(t);
        if (raf) cancelAnimationFrame(raf);
      };
    }, [searchParams]);

    const handleRefresh = async () => {
      setIsRefreshing(true);
      const hideLoading = message.loading('Refreshing benchmark data...', 0);

      try {
        await refetchBenchmarkData();
        hideLoading();
        message.success('Benchmark data refreshed successfully!');
      } catch (error) {
        console.error('Failed to refresh benchmarks:', error);
        hideLoading();
        message.error('Failed to refresh benchmark data. Please try again.');
      } finally {
        setIsRefreshing(false);
      }
    };

    const handleLatentElementsDisplayChange = async (displayType: 'cards' | 'bar_chart' | 'polar' | 'bullet' | 'diverging' | 'slope' | 'scatter' | 'strip') => {
      setIsUpdatingLatentDisplay(true);
      try {
        const updatedPreferences = {
          ...preferences,
          displayTypes: {
            ...displayPreferences,
            latentElements: displayType,
          },
        };
        await updatePreferences({
          jwtToken: user?.jwtToken || '',
          preferences: updatedPreferences,
        }).unwrap();
      } catch (error) {
        console.error('Failed to update display preferences:', error);
        message.error('Failed to save display preference');
      } finally {
        setIsUpdatingLatentDisplay(false);
      }
    };

    const handleMeasuredElementsDisplayChange = async (displayType: 'cards' | 'bar_chart' | 'polar' | 'bullet' | 'diverging' | 'slope' | 'scatter' | 'strip') => {
      setIsUpdatingMeasuredDisplay(true);
      try {
        const updatedPreferences = {
          ...preferences,
          displayTypes: {
            ...displayPreferences,
            measuredElements: displayType,
          },
        };
        await updatePreferences({
          jwtToken: user?.jwtToken || '',
          preferences: updatedPreferences,
        }).unwrap();
      } catch (error) {
        console.error('Failed to update display preferences:', error);
        message.error('Failed to save display preference');
      } finally {
        setIsUpdatingMeasuredDisplay(false);
      }
    };

    // Page chrome (breadcrumb + full-page Layout). Skipped when embedded — the
    // Marketer View shell provides its own chrome.
    const Frame: React.FunctionComponent<{ children: React.ReactNode }> = ({
      children,
    }) =>
      embedded ? (
        <>{children}</>
      ) : (
        <Layout style={{ minHeight: '100vh', background: colorBgContainer }}>
          <Content style={{ padding: '0 50px' }}>
            <Breadcrumb
              style={{ margin: '16px 0' }}
              items={[
                { title: user?.organisation?.name || 'Organization' },
                { title: 'Analytics' },
                { title: 'Benchmarking' },
              ]}
            />
            {children}
          </Content>
        </Layout>
      );

    if (benchmarkLoading) {
      return (
        <Frame>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <Spin size="large" />
          </div>
        </Frame>
      );
    }

    const filteredMetrics = benchmarkData ? getFilteredMetrics(benchmarkData.brandPerformance.metrics) : [];

    // Check if we have meaningful benchmark data
    const hasNoMeaningfulData = !benchmarkAvailable;

    // Placeholder content when benchmarking is not available
    // For tier restrictions, show upgrade prompt without filters
    // For data availability issues, show filters so user can switch business type
    if (hasNoMeaningfulData && !hasOverall) {
      return (
        <Frame>
            <div
              style={{
                padding: 24,
                minHeight: 380,
                background: colorBgContainer,
                borderRadius: borderRadiusLG,
              }}
            >
              <div style={{
                background: 'linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%)',
                padding: 48,
                borderRadius: 12,
                textAlign: 'center',
                border: '1px dashed #d9d9d9'
              }}>
                <div style={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'rgba(245, 158, 11, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px'
                }}>
                  <LockOutlined style={{ fontSize: 36, color: '#F59E0B' }} />
                </div>
                <Title level={3} style={{ marginBottom: 8 }}>
                  Unlock Benchmarking
                </Title>
                <Text type="secondary" style={{ display: 'block', marginBottom: 24, fontSize: 16 }}>
                  Compare your brand's performance against industry benchmarks and competitors.
                </Text>
                <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px', marginBottom: 24 }}>
                  <CrownOutlined style={{ marginRight: 6 }} />
                  {intl.formatMessage({ id: 'subscription.upgrade.required' })}
                </Tag>
                <div>
                  <Button
                    type="primary"
                    size="large"
                    icon={<CrownOutlined />}
                    onClick={() => navigate('/dashboard/organisation?tab=billing&showUpgrade=true')}
                    style={{
                      background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                      border: 'none',
                      borderRadius: 8,
                      height: 48,
                      paddingLeft: 32,
                      paddingRight: 32
                    }}
                  >
                    Upgrade to Professional
                  </Button>
                </div>
              </div>
            </div>
        </Frame>
      );
    }

    // Main benchmarking dashboard
    return (
      <Frame>
          <div
            style={{
              padding: 0,
              minHeight: 380,
              background: 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)',
              borderRadius: borderRadiusLG,
            }}
          >
              {/* Navigation — don't pin to the viewport when embedded in the
                  marketer shell (it has its own sticky top bar). */}
              <div style={{
                padding: '20px 32px',
                background: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(10px)',
                borderBottom: '1px solid rgba(148, 163, 184, 0.1)',
                position: embedded ? 'static' : 'sticky',
                top: 0,
                zIndex: embedded ? 'auto' : 100
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                    <div>
                      <Title level={2} style={{
                        margin: 0,
                        fontSize: '28px',
                        fontWeight: 700,
                        color: '#0f172a',
                        letterSpacing: '-0.025em'
                      }}>
                        {selectedBrand?.name || 'Benchmarking'}
                        {!embedded && <MaturityTag type="earlyAccess" />}
                      </Title>
                      <Text style={{
                        color: '#64748b',
                        fontSize: '16px',
                        fontWeight: 500
                      }}>
                        Brand Performance Benchmarking
                      </Text>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    {!embedded && brands.length > 1 && (
                      <Select
                        value={selectedBrandId}
                        onChange={setSelectedBrandId}
                        style={{ width: 200 }}
                        size="large"
                        options={brands.map(b => ({
                          value: b.id,
                          label: b.name
                        }))}
                      />
                    )}
                    <Tooltip title={!hasProductType ? intl.formatMessage({ id: 'benchmarking.filter.upgradeBusinessType', defaultMessage: 'Upgrade to Professional to filter by business type' }) : undefined}>
                      <Select
                        value={businessTypeFilter}
                        onChange={(value) => {
                          if (value !== 'all' && !hasProductType) {
                            message.info(intl.formatMessage({ id: 'benchmarking.filter.upgradeBusinessType', defaultMessage: 'Upgrade to Professional to filter by business type' }));
                            return;
                          }
                          setBusinessTypeFilter(value);
                        }}
                        style={{ width: 180 }}
                        size="large"
                        placeholder={intl.formatMessage({ id: 'benchmarking.filter.businessTypePlaceholder', defaultMessage: 'Business Type' })}
                        suffixIcon={!hasProductType ? <LockOutlined style={{ color: '#9ca3af' }} /> : undefined}
                      >
                        {businessTypeOptions.map(option => (
                          <Select.Option
                            key={option.value}
                            value={option.value}
                            disabled={option.value !== 'all' && !hasProductType}
                          >
                            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              {option.label}
                              {option.value !== 'all' && !hasProductType && (
                                <LockOutlined style={{ color: '#9ca3af', fontSize: 12, marginLeft: 8 }} />
                              )}
                            </span>
                          </Select.Option>
                        ))}
                      </Select>
                    </Tooltip>
                    <Tooltip title={isProductTypeDisabled ? intl.formatMessage({ id: 'benchmarking.filter.selectBusinessTypeFirst', defaultMessage: 'Select a business type to filter by product type' }) : !hasProductType ? intl.formatMessage({ id: 'benchmarking.filter.upgradeProductType', defaultMessage: 'Upgrade to Professional to filter by product type' }) : undefined}>
                      <Select
                        value={productTypeFilter}
                        onChange={(value) => {
                          if (value !== 'all' && !hasProductType) {
                            message.info(intl.formatMessage({ id: 'benchmarking.filter.upgradeProductType', defaultMessage: 'Upgrade to Professional to filter by product type' }));
                            return;
                          }
                          setProductTypeFilter(value);
                        }}
                        style={{ width: 200 }}
                        size="large"
                        disabled={isProductTypeDisabled}
                        placeholder={intl.formatMessage({ id: 'benchmarking.filter.productTypePlaceholder', defaultMessage: 'Product Type' })}
                        suffixIcon={isProductTypeDisabled || !hasProductType ? <LockOutlined style={{ color: '#9ca3af' }} /> : undefined}
                      >
                        {productTypeOptions.map(option => (
                          <Select.Option
                            key={option.value}
                            value={option.value}
                            disabled={option.value !== 'all' && !hasProductType}
                          >
                            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              {option.label}
                              {option.value !== 'all' && !hasProductType && (
                                <LockOutlined style={{ color: '#9ca3af', fontSize: 12, marginLeft: 8 }} />
                              )}
                            </span>
                          </Select.Option>
                        ))}
                      </Select>
                    </Tooltip>
                    <Tooltip title={!hasAllLevels ? "Upgrade to Enterprise to filter by region" : "Filter benchmarks by region"}>
                      <Select
                        value={selectedCountry}
                        onChange={setSelectedCountry}
                        style={{ width: 200 }}
                        size="large"
                        disabled={!hasAllLevels}
                        placeholder="Select region"
                        options={[
                          { value: 'all', label: 'All Regions' }
                        ]}
                        suffixIcon={
                          !hasAllLevels ? (
                            <LockOutlined style={{ color: '#9ca3af' }} />
                          ) : (
                            <GlobalOutlined />
                          )
                        }
                      />
                    </Tooltip>
                    {!periodOverride && (
                      <Select
                        value={timePeriod}
                        onChange={setTimePeriod}
                        style={{ width: 160 }}
                        options={TIME_PERIODS}
                        size="large"
                      />
                    )}
                    <Button
                      icon={<ReloadOutlined spin={isRefreshing} />}
                      onClick={handleRefresh}
                      loading={isRefreshing}
                      disabled={isRefreshing}
                      size="large"
                      type="default"
                      style={{
                        borderRadius: '8px',
                      }}
                    >
                      {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
                    </Button>
                  </div>
                </div>
                {!embedded && <MaturityAlert type="earlyAccess" style={{ marginTop: 16 }} />}
              </div>

              {/* Data unavailable message - shown with filters visible */}
              {hasNoMeaningfulData && (
                <div style={{
                  padding: '80px 32px',
                  textAlign: 'center',
                }}>
                    <div style={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: 'rgba(245, 158, 11, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 24px'
                    }}>
                      <FundOutlined style={{ fontSize: 36, color: '#F59E0B' }} />
                    </div>
                    <Title level={3} style={{ marginBottom: 16 }}>
                      Benchmarking
                    </Title>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      {benchmarkUnavailableReason || 'Benchmarking features and competitive analysis will be displayed here when available.'}
                    </Text>
                    {benchmarkUnavailableReason && (
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        This typically requires more survey responses or industry data.
                      </Text>
                    )}
                </div>
              )}

              {/* Benchmark data content - hidden when no data */}
              <div style={{ display: hasNoMeaningfulData ? 'none' : undefined }}>
              {/* Hero Metrics Section */}
              <div style={{ padding: '40px 32px 56px' }}>
                {/* Main Performance Score */}
                <div style={{
                  textAlign: 'center',
                  marginBottom: '48px'
                }}>
                  {(() => {
                    const overallMetric = filteredMetrics.find(m => m.level === 0);
                    // Calculate absolute difference instead of percentage difference
                    const brandScore = benchmarkData?.brandPerformance?.overallScore || 0;
                    const industryAvg = overallMetric?.industryMean || 0;
                    const absoluteDiff = calculateAbsoluteDifference(brandScore, industryAvg);

                    // Get performance level based on absolute difference
                    const performanceLevel = getPerformanceLevel(absoluteDiff);

                    // Map performance level to display colors with gradients
                    const getBrandEquityColors = (perfLevel: typeof performanceLevel) => {
                      const colorMaps = {
                        'excellent': {
                          scoreColor: '#15803d',
                          badgeBg: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                          badgeBorder: '#16a34a',
                          badgeColor: '#15803d',
                          iconColor: '#15803d',
                        },
                        'very-good': {
                          scoreColor: '#16a34a',
                          badgeBg: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                          badgeBorder: '#22c55e',
                          badgeColor: '#16a34a',
                          iconColor: '#16a34a',
                        },
                        'good': {
                          scoreColor: '#22c55e',
                          badgeBg: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                          badgeBorder: '#4ade80',
                          badgeColor: '#22c55e',
                          iconColor: '#22c55e',
                        },
                        'fair': {
                          scoreColor: '#ef4444',
                          badgeBg: 'linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)',
                          badgeBorder: '#f87171',
                          badgeColor: '#ef4444',
                          iconColor: '#ef4444',
                        },
                        'poor': {
                          scoreColor: '#dc2626',
                          badgeBg: 'linear-gradient(135deg, #fef2f2 0%, #fca5a5 100%)',
                          badgeBorder: '#ef4444',
                          badgeColor: '#dc2626',
                          iconColor: '#dc2626',
                        },
                        'critical': {
                          scoreColor: '#b91c1c',
                          badgeBg: 'linear-gradient(135deg, #fee2e2 0%, #f87171 100%)',
                          badgeBorder: '#dc2626',
                          badgeColor: '#b91c1c',
                          iconColor: '#b91c1c',
                        }
                      };

                      return {
                        ...colorMaps[perfLevel.performance],
                        description: perfLevel.label
                      };
                    };

                    const colors = getBrandEquityColors(performanceLevel);
                    const isPositive = absoluteDiff >= 0;

                    return (
                      <>
                        <Text style={{
                          fontSize: '16px',
                          fontWeight: 600,
                          color: '#64748b',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          marginBottom: '12px',
                          display: 'block'
                        }}>
                          Brand Equity
                        </Text>
                        <div style={{
                          fontSize: '96px',
                          fontWeight: 800,
                          lineHeight: 1,
                          marginBottom: '8px',
                          color: colors.scoreColor
                        }}>
                          {benchmarkData?.brandPerformance?.overallScore?.toFixed(1) || '0.0'}%
                        </div>
                        <Text style={{
                          fontSize: '14px',
                          fontWeight: 600,
                          color: colors.badgeColor,
                          marginBottom: '16px',
                          display: 'block'
                        }}>
                          {colors.description}
                        </Text>
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '16px',
                          padding: '12px 24px',
                          background: colors.badgeBg,
                          borderRadius: '50px',
                          border: `2px solid ${colors.badgeBorder}`
                        }}>
                          {isPositive ?
                            <RiseOutlined style={{
                              color: colors.iconColor,
                              fontSize: '20px'
                            }} /> :
                            <FallOutlined style={{
                              color: colors.iconColor,
                              fontSize: '20px'
                            }} />
                          }
                          <Text style={{
                            color: colors.badgeColor,
                            fontSize: '18px',
                            fontWeight: 700
                          }}>
                            {isPositive ? '+' : ''}{absoluteDiff.toFixed(1)}% {isPositive ? 'above' : 'below'} industry average
                          </Text>
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Key Metrics Grid - Show all 10 Latent Elements */}
                <div id="benchmark-section-latent" style={{ padding: '0 32px', marginBottom: '32px', scrollMarginTop: 80 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <Title level={4} style={{
                      margin: 0,
                      color: '#1e293b',
                      fontSize: '22px',
                      fontWeight: 700,
                      textShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                      letterSpacing: '0.5px'
                    }}>
                      LATENT ELEMENTS
                    </Title>
                    <Tooltip title={!hasUnrestrictedAccess ? intl.formatMessage({ id: 'subscription.upgrade.additionalChartTypes' }) : undefined}>
                      <Select
                        value={!hasUnrestrictedAccess ? 'cards' : (displayPreferences?.latentElements || 'cards')}
                        onChange={(value) => {
                          if (!hasUnrestrictedAccess && value !== 'cards') {
                            message.info('Upgrade to Professional to access additional chart types');
                            return;
                          }
                          handleLatentElementsDisplayChange(value);
                        }}
                        size="small"
                        style={{ width: 160 }}
                        loading={isUpdatingLatentDisplay}
                        suffixIcon={!hasUnrestrictedAccess ? <LockOutlined style={{ color: '#9ca3af', fontSize: 12 }} /> : undefined}
                      >
                        <Select.Option value="cards">
                          <AppstoreOutlined style={{ marginRight: 6 }} />
                          Cards
                        </Select.Option>
                        <Select.Option value="bar_chart" disabled={!hasUnrestrictedAccess}>
                          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <span><BarChartOutlined style={{ marginRight: 6 }} />Chart</span>
                            {!hasUnrestrictedAccess && <LockOutlined style={{ color: '#9ca3af', fontSize: 11 }} />}
                          </span>
                        </Select.Option>
                        <Select.Option value="polar" disabled={!hasUnrestrictedAccess}>
                          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <span><RadarChartOutlined style={{ marginRight: 6 }} />Polar</span>
                            {!hasUnrestrictedAccess && <LockOutlined style={{ color: '#9ca3af', fontSize: 11 }} />}
                          </span>
                        </Select.Option>
                        <Select.Option value="bullet" disabled={!hasUnrestrictedAccess}>
                          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <span><DashboardOutlined style={{ marginRight: 6 }} />Bullet</span>
                            {!hasUnrestrictedAccess && <LockOutlined style={{ color: '#9ca3af', fontSize: 11 }} />}
                          </span>
                        </Select.Option>
                        <Select.Option value="diverging" disabled={!hasUnrestrictedAccess}>
                          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <span><SplitCellsOutlined style={{ marginRight: 6 }} />Diverging</span>
                            {!hasUnrestrictedAccess && <LockOutlined style={{ color: '#9ca3af', fontSize: 11 }} />}
                          </span>
                        </Select.Option>
                        <Select.Option value="slope" disabled={!hasUnrestrictedAccess}>
                          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <span><LineChartOutlined style={{ marginRight: 6 }} />Slope</span>
                            {!hasUnrestrictedAccess && <LockOutlined style={{ color: '#9ca3af', fontSize: 11 }} />}
                          </span>
                        </Select.Option>
                        <Select.Option value="scatter" disabled={!hasUnrestrictedAccess}>
                          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <span><DotChartOutlined style={{ marginRight: 6 }} />Scatter</span>
                            {!hasUnrestrictedAccess && <LockOutlined style={{ color: '#9ca3af', fontSize: 11 }} />}
                          </span>
                        </Select.Option>
                        <Select.Option value="strip" disabled={!hasUnrestrictedAccess}>
                          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <span><NodeIndexOutlined style={{ marginRight: 6 }} />Strip</span>
                            {!hasUnrestrictedAccess && <LockOutlined style={{ color: '#9ca3af', fontSize: 11 }} />}
                          </span>
                        </Select.Option>
                      </Select>
                    </Tooltip>
                  </div>
                  <Card style={{
                    borderRadius: '16px',
                    boxShadow: '0 8px 32px -8px rgba(0, 0, 0, 0.15)',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    border: '2px solid rgba(148, 163, 184, 0.2)',
                    overflow: 'hidden'
                  }}>
                    <Spin spinning={isUpdatingLatentDisplay} tip="Updating display...">
                      <div style={{
                        padding: '32px',
                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)'
                      }}>
                        {(() => {
                          const latentMetrics = filteredMetrics.filter(m => m.level === 1 && m.categoryName !== 'Brand Equity');
                          // Force cards view for Free plan users
                          const displayType = !hasUnrestrictedAccess ? 'cards' : (displayPreferences?.latentElements || 'cards');

                          switch (displayType) {
                            case 'cards':
                              return <MetricsCards metrics={latentMetrics} maxItems={10} isElementLocked={isElementLocked} />;
                            case 'bar_chart':
                              return <PerformanceChart metrics={latentMetrics} chartId="latent-elements" />;
                            case 'polar':
                              return <PolarChart metrics={latentMetrics} chartId="latent-elements-polar" />;
                            case 'bullet':
                              return <BulletChart metrics={latentMetrics} chartId="latent-elements-bullet" />;
                            case 'diverging':
                              return <DivergingBarChart metrics={latentMetrics} chartId="latent-elements-diverging" />;
                            case 'slope':
                              return <SlopeChart metrics={latentMetrics} chartId="latent-elements-slope" />;
                            case 'scatter':
                              return <ScatterPlot metrics={latentMetrics} chartId="latent-elements-scatter" />;
                            case 'strip':
                              return <StripChart metrics={latentMetrics} chartId="latent-elements-strip" />;
                            default:
                              return <MetricsCards metrics={latentMetrics} maxItems={10} isElementLocked={isElementLocked} />;
                          }
                        })()}
                      </div>
                    </Spin>
                  </Card>
                </div>
              </div>

              {/* Performance Comparison Chart - Full Width */}
              <div id="benchmark-section-measured" style={{ padding: '0 32px', marginBottom: '32px', scrollMarginTop: 80 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                  <Title level={4} style={{
                    margin: 0,
                    color: '#1e293b',
                    fontSize: '22px',
                    fontWeight: 700,
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                    letterSpacing: '0.5px'
                  }}>
                    MEASURED ELEMENTS
                  </Title>
                  <Tooltip title={!hasUnrestrictedAccess ? intl.formatMessage({ id: 'subscription.upgrade.additionalChartTypes' }) : undefined}>
                    <Select
                      value={!hasUnrestrictedAccess ? 'cards' : (displayPreferences?.measuredElements || 'bar_chart')}
                      onChange={(value) => {
                        if (!hasUnrestrictedAccess && value !== 'cards') {
                          message.info('Upgrade to Professional to access additional chart types');
                          return;
                        }
                        handleMeasuredElementsDisplayChange(value);
                      }}
                      size="small"
                      style={{ width: 160 }}
                      loading={isUpdatingMeasuredDisplay}
                      suffixIcon={!hasUnrestrictedAccess ? <LockOutlined style={{ color: '#9ca3af', fontSize: 12 }} /> : undefined}
                    >
                      <Select.Option value="cards">
                        <AppstoreOutlined style={{ marginRight: 6 }} />
                        Cards
                      </Select.Option>
                      <Select.Option value="bar_chart" disabled={!hasUnrestrictedAccess}>
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <span><BarChartOutlined style={{ marginRight: 6 }} />Chart</span>
                          {!hasUnrestrictedAccess && <LockOutlined style={{ color: '#9ca3af', fontSize: 11 }} />}
                        </span>
                      </Select.Option>
                      <Select.Option value="polar" disabled={!hasUnrestrictedAccess}>
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <span><RadarChartOutlined style={{ marginRight: 6 }} />Polar</span>
                          {!hasUnrestrictedAccess && <LockOutlined style={{ color: '#9ca3af', fontSize: 11 }} />}
                        </span>
                      </Select.Option>
                      <Select.Option value="bullet" disabled={!hasUnrestrictedAccess}>
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <span><DashboardOutlined style={{ marginRight: 6 }} />Bullet</span>
                          {!hasUnrestrictedAccess && <LockOutlined style={{ color: '#9ca3af', fontSize: 11 }} />}
                        </span>
                      </Select.Option>
                      <Select.Option value="diverging" disabled={!hasUnrestrictedAccess}>
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <span><SplitCellsOutlined style={{ marginRight: 6 }} />Diverging</span>
                          {!hasUnrestrictedAccess && <LockOutlined style={{ color: '#9ca3af', fontSize: 11 }} />}
                        </span>
                      </Select.Option>
                      <Select.Option value="slope" disabled={!hasUnrestrictedAccess}>
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <span><LineChartOutlined style={{ marginRight: 6 }} />Slope</span>
                          {!hasUnrestrictedAccess && <LockOutlined style={{ color: '#9ca3af', fontSize: 11 }} />}
                        </span>
                      </Select.Option>
                      <Select.Option value="scatter" disabled={!hasUnrestrictedAccess}>
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <span><DotChartOutlined style={{ marginRight: 6 }} />Scatter</span>
                          {!hasUnrestrictedAccess && <LockOutlined style={{ color: '#9ca3af', fontSize: 11 }} />}
                        </span>
                      </Select.Option>
                      <Select.Option value="strip" disabled={!hasUnrestrictedAccess}>
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <span><NodeIndexOutlined style={{ marginRight: 6 }} />Strip</span>
                          {!hasUnrestrictedAccess && <LockOutlined style={{ color: '#9ca3af', fontSize: 11 }} />}
                        </span>
                      </Select.Option>
                    </Select>
                  </Tooltip>
                </div>
                <Card style={{
                  borderRadius: '16px',
                  boxShadow: '0 8px 32px -8px rgba(0, 0, 0, 0.15)',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  border: '2px solid rgba(148, 163, 184, 0.2)',
                  overflow: 'hidden'
                }}>
                  <Spin spinning={isUpdatingMeasuredDisplay} tip="Updating display...">
                    <div style={{
                      padding: '32px',
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)'
                    }}>
                      {(() => {
                        const measuredMetrics = filteredMetrics.filter(m => m.level === 2 && m.categoryName !== 'Price Deals' && m.categoryName !== 'Non-customer Awareness');
                        // Force cards view for Free plan users
                        const displayType = !hasUnrestrictedAccess ? 'cards' : (displayPreferences?.measuredElements || 'bar_chart');

                        switch (displayType) {
                          case 'bar_chart':
                            return <PerformanceChart metrics={measuredMetrics} chartId="measured-elements" />;
                          case 'cards':
                            return <MetricsCards metrics={measuredMetrics} isElementLocked={isElementLocked} />;
                          case 'polar':
                            return <PolarChart metrics={measuredMetrics} chartId="measured-elements-polar" />;
                          case 'bullet':
                            return <BulletChart metrics={measuredMetrics} chartId="measured-elements-bullet" />;
                          case 'diverging':
                            return <DivergingBarChart metrics={measuredMetrics} chartId="measured-elements-diverging" />;
                          case 'slope':
                            return <SlopeChart metrics={measuredMetrics} chartId="measured-elements-slope" />;
                          case 'scatter':
                            return <ScatterPlot metrics={measuredMetrics} chartId="measured-elements-scatter" />;
                          case 'strip':
                            return <StripChart metrics={measuredMetrics} chartId="measured-elements-strip" />;
                          default:
                            return <MetricsCards metrics={measuredMetrics} isElementLocked={isElementLocked} />;
                        }
                      })()}
                    </div>
                  </Spin>
                </Card>
              </div>
              </div>{/* end benchmark data content wrapper */}

          </div>
      </Frame>
    );
};

export default BenchmarkingPage;
